import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  browserName,
  browserVersion,
  isMobile,
  mobileModel,
  mobileVendor,
  osName,
  osVersion,
} from 'react-device-detect';

import { LOCALIZATION } from 'constants/localization';
import { StepperTab } from './StepperTab';
import Modal from 'components/common/Modal';
import { ARTICLES, PAGE_TITLES } from 'constants/constants';
import { AxiosError, TitleContentPair } from 'interfaces/interfaces';
import sharedService from 'services/sharedService';
import { InitStore, useInitStore } from 'stores/useInitStore';
import authService from 'services/authService';
import { UserStore, useUserStore } from 'stores/useUserStore';
import { NotificationsStore, useNotificationsStore } from 'stores/useNotificationsStore';
import Notification from 'components/common/Notification';
import profileService from 'services/profileService';

interface State {
  userName: string;
  password: string;
  confirmPassword: string;
  selectedQuestion: string;
  selectedAnswer: string;
  isAgree: boolean;
  hideNewPassword: boolean;
  hideConfirmPassword: boolean;
  pageTitle: string;
  isFormValid: boolean;
  isTermsAndConditionsModalOpen: boolean;
  isPrivacyPolicyModalOpen: boolean;
  globalWebContents: Record<string, string>;
  validations: {
    greaterThan8: boolean;
    hasLowerCase: boolean;
    hasUpperCase: boolean;
    hasNumber: boolean;
    newConfirmMatch: boolean;
  };
}

interface Question {
  question: string;
  questionId: number;
}

export const SignInCreds = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<State>({
    userName: '',
    password: '',
    confirmPassword: '',
    selectedQuestion: '',
    selectedAnswer: '',
    isAgree: false,
    pageTitle: '',
    isFormValid: false,
    isTermsAndConditionsModalOpen: false,
    isPrivacyPolicyModalOpen: false,
    hideNewPassword: true,
    hideConfirmPassword: true,
    globalWebContents: {},
    validations: {
      greaterThan8: false,
      hasLowerCase: false,
      hasUpperCase: false,
      hasNumber: false,
      newConfirmMatch: false,
    },
  });
  const { initData, setAppLoading, clientGroupsData } = useInitStore((state: InitStore) => ({
    initData: state.initData,
    setAppLoading: state.setAppLoading,
    clientGroupsData: state.clientGroupsData,
  }));

  const { processMessages, notificationsData, setInterScreenNotification } = useNotificationsStore(
    (state: NotificationsStore) => ({
      processMessages: state.processMessages,
      notificationsData: state.notificationsData,
      setInterScreenNotification: state.setInterScreenNotification,
    }),
  );

  const { setUser } = useUserStore((state: UserStore) => ({
    setUser: state.setUser,
  }));

  const handleTermsAndConditionsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setState((prevState) => ({ ...prevState, isTermsAndConditionsModalOpen: true }));
  };

  const handlePrivacyPolicyClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setState((prevState) => ({ ...prevState, isPrivacyPolicyModalOpen: true }));
  };

  const checkRequiredFields = (state: State) => {
    if (/^\s*$/.test(state.userName)) {
      setState((prevState) => ({ ...prevState, isFormValid: false }));
      return false;
    }

    if (/^\s*$/.test(state.password)) {
      setState((prevState) => ({ ...prevState, isFormValid: false }));
      return false;
    }

    if (/^\s*$/.test(state.confirmPassword)) {
      setState((prevState) => ({ ...prevState, isFormValid: false }));
      return false;
    }

    if (/^\s*$/.test(state.selectedQuestion)) {
      setState((prevState) => ({ ...prevState, isFormValid: false }));
      return false;
    }

    if (/^\s*$/.test(state.selectedAnswer)) {
      setState((prevState) => ({ ...prevState, isFormValid: false }));
      return false;
    }

    if (!state.isAgree) {
      setState((prevState) => ({ ...prevState, isFormValid: false }));
      return false;
    }

    setState((prevState) => ({ ...prevState, isFormValid: true }));
    return true;
  };

  const passwordValidation = (state: State) => {
    const greaterThan8 = state.password.length >= 8;
    const hasLowerCase = state.password.match(/^(.*[a-z].*)$/);
    const hasUpperCase = state.password.match(/^(.*[A-Z].*)$/);
    const hasNumber = !state.password.match(/^([^0-9]*)$/);
    const newConfirmMatch =
      state.password !== '' && state.confirmPassword !== '' && state.password === state.confirmPassword;

    setState((prevState) => ({
      ...prevState,
      validations: {
        greaterThan8: !!greaterThan8,
        hasLowerCase: !!hasLowerCase,
        hasUpperCase: !!hasUpperCase,
        hasNumber,
        newConfirmMatch,
      },
    }));

    if (greaterThan8 && hasLowerCase && hasUpperCase && hasNumber && newConfirmMatch) {
      setState((prevState) => ({ ...prevState, isFormValid: true }));
      return true;
    } else {
      setState((prevState) => ({ ...prevState, isFormValid: false }));
      return false;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    setState((prevState) => {
      const updatedState = { ...prevState, [name]: value };
      const isValid = passwordValidation(updatedState);
      if (isValid) {
        checkRequiredFields(updatedState);
      }
      return updatedState;
    });
  };

  const handleRememberChkBox = () => {
    setState((prevState) => {
      const updatedState = { ...prevState, isAgree: !prevState.isAgree };
      const isValid = passwordValidation(updatedState);
      if (isValid) {
        checkRequiredFields(updatedState);
      }
      return updatedState;
    });
  };

  const handleSubmit = async () => {
    const isPasswordValid = passwordValidation(state);
    const isRequiredValid = checkRequiredFields(state);
    if (isPasswordValid && isRequiredValid && state.isAgree) {
      try {
        setAppLoading(true);
        const hostName = document.location.hostname;
        const ipResponse = await authService.getPublicIP();
        localStorage.setItem('customerLoginIp', ipResponse.data?.ip);
        const params = {
          hostName: hostName,
          planNumber: location.state.planNumber,
          accountNumber: location.state.accountNumber,
          firstName: location.state.firstName,
          lastName: location.state.lastName,
          email: location.state.email,
          phoneNumber: location.state.phoneNumber,
          zipcode: location.state.zipcode,
          username: state.userName,
          password: state.password,
          securityQuestion: state.selectedQuestion,
          securityAnswer: state.selectedAnswer,
          client: location.state.client,
          clientName: location.state.clientName,
          personNumber: location.state.personNumber,
          clientLoginIp: ipResponse.data?.ip,
          userDeviceInfo: {
            userOS: osName,
            userOSVersion: osVersion,
            browserName: browserName,
            browserVersion: browserVersion,
            isMobile: isMobile ? 'Y' : 'N',
            mobileModel: mobileModel,
            mobileVendor: mobileVendor,
          },
        };

        const response = await authService.verifySignInCredsAndCompleteRegistration(params);
        if (response.status === 200 && response.data?.access_token) {
          setUser(response.data);

          const updateParams = {
            username: state.userName, // service will combine with client
            isCustomerUser: false,
            client: location.state.client,
            clientLoginIp: ipResponse.data?.ip,
            userDeviceInfo: {
              userOS: osName,
              userOSVersion: osVersion,
              browserName: browserName,
              browserVersion: browserVersion,
              isMobile: isMobile ? 'Y' : 'N',
              mobileModel: mobileModel,
              mobileVendor: mobileVendor,
            },
          };

          await authService.updateUserInformation(updateParams);

          const updateEmailparams = {
            addressLine1: '',
            addressLine2: '',
            addressLine3: '',
            cityName: '',
            stateCode: '',
            zipCode: '',
            phoneNumber: '',
            emailAddress: response.data.email,
            personNumber: location.state.personNumber,
            username: response.data.userid,
            clientLoginIp: ipResponse.data?.ip,
            hostName: hostName,
            serviceInvoker: 'registration',
          };
          await profileService.updateProfile(updateEmailparams);

          localStorage.setItem('hideWelcomeTxt', 'true');
          if (initData?.isClientGroup) {
            const selectedUnionHostname = clientGroupsData?.items?.find(
              (item) => item.subGroup === location.state.client,
            )?.hostName;
            localStorage.setItem('selectedUnionHostname', selectedUnionHostname as string);
            window.open(`https://${selectedUnionHostname}/logging-in/${btoa(JSON.stringify(response.data))}`, '_self');
          } else {
            navigate('/dashboard');
          }
        } else {
          processMessages(response.data.messages);
        }
      } catch (error) {
        console.error(error);
        const axiosError = error as AxiosError;
        processMessages(axiosError?.response?.data?.messages);
      } finally {
        setAppLoading(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  useEffect(() => {
    const { clientName } = initData ?? {};
    setState((prevState) => ({ ...prevState, pageTitle: clientName + PAGE_TITLES.REGISTRATION_LOGIN }));
    const getContent = async (titles: string[]) => {
      try {
        setAppLoading(true);
        const params = {
          titles,
        };
        const response = await sharedService.getContents(params);
        if (response.status === 200) {
          const webContents: Record<string, string> = {};
          response.data.items.forEach((element: TitleContentPair) => {
            webContents[element.title] = element.content;
          });
          setState((prevState) => ({ ...prevState, globalWebContents: webContents }));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setAppLoading(false);
      }
    };
    getContent([ARTICLES.TERMS_OF_SERVICE, ARTICLES.PRIVACY_POLICY]);
  }, [initData, setAppLoading]);

  useEffect(() => {
    if (!location.state) {
      navigate('/account-details');
    }
  }, [navigate, location.state]);

  useEffect(() => {
    if (notificationsData?.interScreenNotification?.length) {
      processMessages(notificationsData?.interScreenNotification);
      setInterScreenNotification([]);
    }
  }, [notificationsData?.interScreenNotification, processMessages, setInterScreenNotification]);

  if (!location.state) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>
          {
            // this.props.globalContext.state.pagesTitle[PAGES_TITLE_KEYS.REGISTRATION_PERSONAL] ||
            state.pageTitle
          }
        </title>
      </Helmet>
      <div className="registerWrapper ">
        <div className="registerHeader">
          <h1 className="mainHeadingT">{LOCALIZATION.EN.REGISTRATION_ALL_STEPS}</h1>
          {!!notificationsData.nonInfoNotificaiton.length &&
            notificationsData.nonInfoNotificaiton.map((notification, index) => (
              <Notification key={index} id={index} message={notification} />
            ))}
        </div>
        <StepperTab step={4} />

        <div className="usernameWrapper">
          <div className="lusernameColumn mobileButton loginCred">
            <div className="mt-3 mb-3 mobileView">
              <strong>Step 4: </strong> Login Credentials
            </div>
            <div className="backgroundSetColor">
              <div className="columnLayout">
                <div className="leftCol">
                  <div className="form-group">
                    <div className="username mt-3">
                      <label htmlFor="username"> Username</label>
                      <input
                        onKeyDown={handleKeyDown}
                        className="form-control"
                        type="text"
                        id="Username"
                        onChange={handleChange}
                        placeholder="Username"
                        name="userName"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <div className="password mt-3">
                      <label htmlFor="password">Password</label>
                      <input
                        onKeyDown={handleKeyDown}
                        className="form-control password"
                        type={state.hideNewPassword ? 'password' : 'text'}
                        onChange={handleChange}
                        id="password"
                        placeholder="Password"
                        name="password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setState((prevState) => ({
                            ...prevState,
                            hideNewPassword: !state.hideNewPassword,
                          }));
                        }}
                        className="showHide"
                      >
                        <i className={'far ' + (state.hideNewPassword ? 'fa-eye' : 'fa-eye-slash')}></i>
                      </button>
                    </div>
                  </div>
                  <div className="password-validation-ctnr mobileView">
                    <span>Password must have: </span> <br></br>
                    <span className={state.validations.greaterThan8 ? 'valid' : 'invalid'}>
                      at least 8 characters in length
                    </span>
                    <br></br>
                    <span className={state.validations.hasLowerCase ? 'valid' : 'invalid'}>
                      at least 1 lowercase letter (a-z)
                    </span>
                    <br></br>
                    <span className={state.validations.hasUpperCase ? 'valid' : 'invalid'}>
                      at least 1 uppercase letter (A-Z)
                    </span>
                    <br></br>
                    <span className={state.validations.hasNumber ? 'valid' : 'invalid'}>
                      at least 1 number (i.e. 0-9)
                    </span>
                    <br></br>
                    <span className={state.validations.newConfirmMatch ? 'valid' : 'invalid'}>
                      New and Confirm password must match
                    </span>
                    <br></br>
                  </div>
                  <div className="form-group">
                    <div className="confirmpassword mt-3">
                      <label htmlFor="confirmpassword">Confirm Password</label>
                      <input
                        onKeyDown={handleKeyDown}
                        className="form-control password"
                        onChange={handleChange}
                        type={state.hideConfirmPassword ? 'password' : 'text'}
                        id="confirmpassword"
                        placeholder="Confirm Password"
                        name="confirmPassword"
                        required
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setState((prevState) => ({
                            ...prevState,
                            hideConfirmPassword: !state.hideConfirmPassword,
                          }))
                        }
                        className="showHide"
                      >
                        <i className={'far ' + (state.hideConfirmPassword ? 'fa-eye' : 'fa-eye-slash')}></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="leftCol desktopView">
                  <div className="password-validation-ctnr">
                    <span>Password must have: </span> <br></br>
                    <span className={state.validations.greaterThan8 ? 'valid' : 'invalid'}>
                      at least 8 characters in length
                    </span>
                    <br></br>
                    <span className={state.validations.hasLowerCase ? 'valid' : 'invalid'}>
                      at least 1 lowercase letter (a-z)
                    </span>
                    <br></br>
                    <span className={state.validations.hasUpperCase ? 'valid' : 'invalid'}>
                      at least 1 uppercase letter (A-Z)
                    </span>
                    <br></br>
                    <span className={state.validations.hasNumber ? 'valid' : 'invalid'}>
                      at least 1 number (i.e. 0-9)
                    </span>
                    <br></br>
                    <span className={state.validations.newConfirmMatch ? 'valid' : 'invalid'}>
                      New and Confirm password must match
                    </span>
                    <br></br>
                  </div>
                </div>
              </div>
            </div>
            <div className="backgroundSetColor">
              <div className="formLayout">
                <h2 className="headingText">Security Question and Answer</h2>
                <div className="securityQuestion">
                  <div>Select a security question</div>
                  <select
                    value={state.selectedQuestion}
                    name="selectedQuestion"
                    onChange={handleChange}
                    className="questionAnswer"
                  >
                    <option value="">Please Select</option>
                    {location.state?.questions?.map((value: Question) => (
                      <option key={value.questionId} value={value.question}>
                        {value.question}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <div className="answer">
                    <label htmlFor="answer">Answer</label>
                    <input
                      onKeyDown={handleKeyDown}
                      className="form-control"
                      type="text"
                      id="answer"
                      onChange={handleChange}
                      name="selectedAnswer"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="privacyPolicy">
              <input
                onKeyDown={handleKeyDown}
                type="checkbox"
                name="iAgree"
                checked={state.isAgree}
                onChange={handleRememberChkBox}
              />
              <span className="material-icons blankCheckbox">check_box_outline_blank</span>
              <span className="material-icons selectedCheckbox">check_box</span>
              <span>
                I agree to the AGIA&nbsp;
                <Link title="Terms and Conditions" to="#" onClick={handleTermsAndConditionsClick}>
                  Terms of Service
                </Link>
                &nbsp;and&nbsp;
                <Link title="Privacy Policy" to="#" onClick={handlePrivacyPolicyClick}>
                  Privacy Policy
                </Link>
              </span>
            </div>
            <div className="roundBottomButton mt-3">
              <Link
                id="ma-ra-step4Login"
                state={location.state}
                to={{
                  pathname: `/personal-info`,
                }}
              >
                Back
              </Link>
              <button
                id="ma-ra-step4"
                disabled={!(state.isFormValid && state.isAgree)}
                className="btn btn-primary agiaBlueButton"
                onClick={handleSubmit}
                value="Create Account"
              >
                Finish registering my account
              </button>
            </div>
          </div>
        </div>
      </div>

      {state.isTermsAndConditionsModalOpen && (
        <Modal
          title="Terms and Conditions"
          onClose={() => setState((prevState) => ({ ...prevState, isTermsAndConditionsModalOpen: false }))}
        >
          <p
            dangerouslySetInnerHTML={{ __html: state.globalWebContents[ARTICLES.TERMS_OF_SERVICE] }}
            id={`globalWC_${ARTICLES.TERMS_OF_SERVICE}`}
            className="noteText"
          />
        </Modal>
      )}
      {state.isPrivacyPolicyModalOpen && (
        <Modal
          title="Privacy Policy"
          onClose={() => setState((prevState) => ({ ...prevState, isPrivacyPolicyModalOpen: false }))}
        >
          <p
            dangerouslySetInnerHTML={{ __html: state.globalWebContents[ARTICLES.PRIVACY_POLICY] }}
            id={`globalWC_${ARTICLES.PRIVACY_POLICY}`}
            className="noteText"
          />
        </Modal>
      )}
    </>
  );
};
